import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, of, from, firstValueFrom } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";

import { SecuredContentsService } from "@skills-app/services/secured-contents.service";

/**
 * Global photo loader with:
 * - Concurrency limit
 * - In-memory cache
 * - TTL-based cache entries (including negative cache)
 * - Short backoff for transient failures (429/503)
 *
 * Returns Blob|null (null means "use fallback")
 */
@Injectable({ providedIn: "root" })
export class ProfilePhotoLoaderService {
  private readonly cache = new Map<
    string,
    { expiresAt: number; obs$: Observable<Blob | null> }
  >();

  // Concurrency conservative; avoids hammering /api/contents/profilephoto/*
  private readonly limit = 3;
  private active = 0;
  private readonly waiting: Array<() => void> = [];

  constructor(
    private readonly securedContentsService: SecuredContentsService,
  ) {}

  public getPhoto(key: string): Observable<Blob | null> {
    const normalized = (key ?? "").trim();
    if (!normalized) return of(null);

    const now = Date.now();
    const existing = this.cache.get(normalized);
    if (existing && existing.expiresAt > now) {
      return existing.obs$;
    }
    if (existing) this.cache.delete(normalized);

    // pessimistic default TTL until we know the outcome (2 minutes)
    const entry = {
      expiresAt: now + 2 * 60_000,
      obs$: of<Blob | null>(null),
    };

    const obs$ = from(
      this.runWithLimit(() => this.fetchPhotoWithTtl(normalized)),
    ).pipe(
      tap((result) => {
        entry.expiresAt = Date.now() + result.ttlMs;
      }),
      map((result) => result.blob),
      // Cache the resolved value (including null) for the TTL above
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    entry.obs$ = obs$;
    this.cache.set(normalized, entry);

    return obs$;
  }

  private async fetchPhotoWithTtl(
    key: string,
  ): Promise<{ blob: Blob | null; ttlMs: number }> {
    const res = await firstValueFrom(
      this.securedContentsService.getProfilePhoto(key).pipe(
        map((blob) => ({ blob, status: 200 as const })),
        catchError((err: unknown) => {
          const http = err as Partial<HttpErrorResponse>;
          const status = typeof http?.status === "number" ? http.status : 0;
          return of({ blob: null as Blob | null, status });
        }),
      ),
    );

    const status = res.status;

    // Treat null/empty blob as "no photo"
    if (!res.blob) {
      // 404 = no photo: keep a long negative cache to avoid spamming lists
      if (status === 404) return { blob: null, ttlMs: 24 * 60 * 60_000 }; // 24h

      // 401/403 often indicates session/auth issues; retry sooner
      if (status === 401 || status === 403)
        return { blob: null, ttlMs: 10 * 60_000 }; // 10m

      // Transient/unhealthy upstream: avoid retry storms
      if (status === 429 || status === 503)
        return { blob: null, ttlMs: 2 * 60_000 }; // 2m

      // Unknown/non-http: short retry window
      if (status === 0) return { blob: null, ttlMs: 2 * 60_000 }; // 2m

      // Other errors: cache a bit longer to prevent repeated churn
      return { blob: null, ttlMs: 10 * 60_000 }; // 10m
    }

    if (res.blob instanceof Blob && res.blob.size === 0) {
      // Empty blob behaves like "no photo"
      return { blob: null, ttlMs: 24 * 60 * 60_000 }; // 24h
    }

    return { blob: res.blob, ttlMs: 60 * 60_000 }; // 1h positive cache
  }

  private async runWithLimit<T>(fn: () => Promise<T>): Promise<T> {
    if (this.active >= this.limit) {
      await new Promise<void>((resolve) => this.waiting.push(resolve));
    }

    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      const next = this.waiting.shift();
      if (next) next();
    }
  }
}
